import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Popover } from '@televet/kibble-ui/build/components/Popover';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import React, { useMemo } from 'react';

interface DisputePopoverProps {
  stripeId?: string | null;
  hasAdminRoleAtCurrentClinic: boolean;
  isDisputeDrawerLoading: boolean;
  onSubmit: () => void;
  isCareDispute?: boolean;
}

export const DisputePopover = ({
  stripeId,
  hasAdminRoleAtCurrentClinic,
  isDisputeDrawerLoading,
  onSubmit,
  isCareDispute,
}: DisputePopoverProps): JSX.Element => {
  const isManageDisputeDisabled = useMemo(
    () => !hasAdminRoleAtCurrentClinic || !stripeId,
    [hasAdminRoleAtCurrentClinic, stripeId],
  );
  return (
    <Popover
      anchor={
        <Flex ml={1} className="dispute-popover-anchor" cursor="pointer">
          <Icon name="infoCircle" variant="default" size="sm" />
        </Flex>
      }
      size="lg"
      variant="default"
      headerProps={{ children: 'Disputed Payment' }}
      footerProps={{
        hideButtons: true,
        children: (
          <Flex justifyContent="flex-end">
            <Tooltip
              label={
                !hasAdminRoleAtCurrentClinic
                  ? 'Dispute management is only available for clinic admins'
                  : isCareDispute
                  ? 'Care disputes are temporarily managed by providing details and evidence to kennedy@otto.vet.'
                  : 'Dispute management is temporarily disabled, please contact Otto Support.'
              }
              isDisabled={!isCareDispute && !isManageDisputeDisabled}
            >
              <Box>
                <Button
                  size="sm"
                  isLoading={isDisputeDrawerLoading}
                  isDisabled={isCareDispute || isManageDisputeDisabled}
                  onClick={onSubmit}
                >
                  Manage Dispute
                </Button>
              </Box>
            </Tooltip>
          </Flex>
        ),
      }}
    >
      <Text as="p" size="sm" whiteSpace="initial" mb={4}>
        This payment has been disputed by the client. If you believe the charge is legitimate, you can counter the
        dispute by providing details and evidence.
      </Text>
      <Link
        size="sm"
        isExternal
        fontWeight="semibold"
        textDecoration="none"
        iconProps={{ name: 'externalLink', position: 'end' }}
        href="https://help.otto.vet/hc/en-us/articles/34525885110157-Handling-Payment-Disputes-in-Flow"
      >
        Learn how to handle disputes
      </Link>
    </Popover>
  );
};
